// src/pages/auth/LoginPage.tsx
import React, { useState } from 'react';
import { Eye, EyeOff, Mail, Lock, AlertCircle, Loader } from 'lucide-react';
import { authService } from '../../services/authService';
import { TermsModal } from '../../components/legal/TermsModal';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
  acceptedTerms: boolean;
}

interface ValidationErrors {
  email?: string;
  password?: string;
  acceptedTerms?: string;
  general?: string;
  errorCode?: string; // Track error code for conditional UI
}

interface LoginPageProps {
  onLogin?: (credentials: LoginFormData) => Promise<void>;
  onNavigateToRegister?: () => void;
  onNavigateToForgotPassword?: () => void;
  onLoginSuccess?: () => void;
  embedded?: boolean; // If true, render without full-page wrapper
}

export const LoginPage: React.FC<LoginPageProps> = ({
  onLogin,
  onNavigateToRegister,
  onNavigateToForgotPassword,
  onLoginSuccess,
  embedded = false
}) => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false,
    acceptedTerms: false
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsModalTab, setTermsModalTab] = useState<'terms' | 'privacy'>('terms');

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.acceptedTerms) {
      newErrors.acceptedTerms = 'You must accept the Terms & Conditions to continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear field-specific errors
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    // Only clear general error if user is actively changing email or password
    // (not just focus/blur events or checkbox changes)
    if (errors.general && (name === 'email' || name === 'password') && value !== formData[name as keyof LoginFormData]) {
      setErrors(prev => ({ ...prev, general: undefined, errorCode: undefined }));
    }

    // Clear resend success message when user starts typing
    if (resendSuccess) {
      setResendSuccess(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email || resendingEmail) return;

    setResendingEmail(true);
    setResendSuccess(false);

    try {
      await authService.resendVerification(formData.email);
      setResendSuccess(true);
      // Clear success message after 5 seconds
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error: any) {
      console.error('Resend verification error:', error);
      setErrors({
        general: error.message || 'Failed to resend verification email. Please try again.',
        errorCode: ''
      });
    } finally {
      setResendingEmail(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    // Don't clear errors here - only clear on user input

    try {
      if (onLogin) {
        await onLogin(formData);
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        await new Promise(resolve => setTimeout(resolve, 2000));
        // Login successful (mock mode)
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      }
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      const errorCode = error?.code || error?.error?.code || '';

      let displayError = 'Login failed. Please try again.';

      // Handle specific error codes from backend
      if (errorCode === 'USER_NOT_FOUND' ||
        errorMessage.toLowerCase().includes('no account found')) {
        displayError = 'No account found with this email address. Please check your email or create a new account.';
      } else if (errorCode === 'INVALID_CREDENTIALS' ||
        errorMessage.toLowerCase().includes('incorrect password') ||
        errorMessage.toLowerCase().includes('invalid login credentials')) {
        displayError = 'Incorrect password. Please try again or use "Forgot Password" to reset it.';
      } else if (errorCode === 'EMAIL_NOT_VERIFIED' ||
        errorMessage.toLowerCase().includes('email not verified') ||
        errorMessage.toLowerCase().includes('verify your email')) {
        displayError = 'Please verify your email address before logging in. Check your inbox for a verification email.';
      } else if (errorCode === 'AUTHENTICATION_FAILED' ||
        errorMessage.toLowerCase().includes('authentication failed')) {
        displayError = 'Authentication failed. Please check your credentials and try again.';
      } else if (errorCode === 'RATE_LIMITED' ||
        errorMessage.toLowerCase().includes('too many') ||
        errorMessage.toLowerCase().includes('rate limit')) {
        displayError = 'Too many login attempts. Please wait a few minutes before trying again.';
      } else if (errorCode === 'NETWORK_ERROR' || errorMessage.toLowerCase().includes('network') ||
        errorMessage.toLowerCase().includes('fetch')) {
        displayError = 'Network error. Please check your internet connection and try again.';
      } else if (errorMessage) {
        displayError = errorMessage;
      }

      setErrors({ general: displayError, errorCode });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: embedded ? 'auto' : '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: embedded ? 'transparent' : 'var(--color-background)',
      padding: embedded ? '0' : 'var(--space-6)',
      fontFamily: 'var(--font-primary)'
    }}>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Custom checkbox styling for better visibility in dark mode */
        input[type="checkbox"].custom-checkbox {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          width: 16px;
          height: 16px;
          border: 2px solid var(--color-border);
          border-radius: 3px;
          cursor: pointer;
          position: relative;
          background-color: var(--color-background);
          transition: all 0.15s ease-in-out;
        }

        input[type="checkbox"].custom-checkbox:checked {
          background-color: #3b82f6;
          border-color: #3b82f6;
        }

        input[type="checkbox"].custom-checkbox:checked::after {
          content: '';
          position: absolute;
          left: 4px;
          top: 1px;
          width: 4px;
          height: 8px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }

        input[type="checkbox"].custom-checkbox:hover:not(:disabled) {
          border-color: #3b82f6;
        }

        input[type="checkbox"].custom-checkbox:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>

      <div style={{ width: '100%', maxWidth: '400px' }}>
        {/* Logo - only show when not embedded */}
        {!embedded && (
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
            <h1 style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--color-primary)',
              margin: '0 0 var(--space-2) 0',
              letterSpacing: 'var(--tracking-tight)'
            }}>
              heal
            </h1>
            <p style={{
              fontFamily: 'var(--font-primary)',
              fontSize: 'var(--text-base)',
              color: 'var(--color-primary)',
              margin: 0
            }}>
              Sign in to your health companion
            </p>
          </div>
        )}

        {/* Login Form */}
        <div style={{
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          padding: 'var(--space-8)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {/* General Error Message */}
            {errors.general && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-3)'
              }} className="animate-shake">
                <AlertCircle size={20} color="var(--color-error)" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <p style={{
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: '#991b1b',
                    margin: 0
                  }}>
                    {errors.general}
                  </p>

                  {/* Show helpful actions based on error type */}
                  {errors.errorCode === 'EMAIL_NOT_VERIFIED' && (
                    <p style={{
                      fontFamily: 'var(--font-primary)',
                      fontSize: 'var(--text-xs)',
                      color: '#b91c1c',
                      margin: 'var(--space-2) 0 0 0'
                    }}>
                      Didn't receive the email? Check your spam folder or{' '}
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendingEmail}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: resendingEmail ? '#9ca3af' : '#667eea',
                          textDecoration: 'underline',
                          cursor: resendingEmail ? 'not-allowed' : 'pointer',
                          padding: 0,
                          fontFamily: 'inherit',
                          fontSize: 'inherit'
                        }}
                      >
                        {resendingEmail ? 'sending...' : 'resend verification email'}
                      </button>
                    </p>
                  )}

                  {/* Success message for resent email */}
                  {resendSuccess && (
                    <p style={{
                      fontFamily: 'var(--font-primary)',
                      fontSize: 'var(--text-xs)',
                      color: '#15803d',
                      margin: 'var(--space-2) 0 0 0',
                      fontWeight: 'var(--font-medium)'
                    }}>
                      ✓ Verification email sent! Check your inbox.
                    </p>
                  )}

                  {errors.errorCode === 'USER_NOT_FOUND' && (
                    <p style={{
                      fontFamily: 'var(--font-primary)',
                      fontSize: 'var(--text-xs)',
                      color: '#b91c1c',
                      margin: 'var(--space-2) 0 0 0'
                    }}>
                      Don't have an account?{' '}
                      <button
                        type="button"
                        onClick={onNavigateToRegister}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#667eea',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          fontFamily: 'inherit',
                          fontSize: 'inherit'
                        }}
                      >
                        Create one now
                      </button>
                    </p>
                  )}

                  {errors.errorCode === 'INVALID_CREDENTIALS' && (
                    <p style={{
                      fontFamily: 'var(--font-primary)',
                      fontSize: 'var(--text-xs)',
                      color: '#b91c1c',
                      margin: 'var(--space-2) 0 0 0'
                    }}>
                      Forgot your password?{' '}
                      <button
                        type="button"
                        onClick={onNavigateToForgotPassword}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#667eea',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          padding: 0,
                          fontFamily: 'inherit',
                          fontSize: 'inherit'
                        }}
                      >
                        Reset it here
                      </button>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" style={{
                display: 'block',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-primary)',
                marginBottom: 'var(--space-2)'
              }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={20} color="var(--color-muted)" style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }} />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    paddingLeft: 'var(--space-10)',
                    paddingRight: 'var(--space-4)',
                    paddingTop: 'var(--space-3)',
                    paddingBottom: 'var(--space-3)',
                    border: `1px solid ${errors.email ? 'var(--color-error)' : 'var(--color-border)'}`,
                    borderRadius: 'var(--radius-lg)',
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text)',
                    outline: 'none',
                    transition: 'var(--transition-fast)',
                    backgroundColor: isLoading ? 'var(--color-surface)' : 'var(--color-background)',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    if (!errors.email) {
                      e.currentTarget.style.borderColor = 'var(--color-primary)';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.email ? 'var(--color-error)' : 'var(--color-border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
              {errors.email && (
                <p style={{
                  marginTop: 'var(--space-1)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)'
                }}>
                  <AlertCircle size={16} />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" style={{
                display: 'block',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-primary)',
                marginBottom: 'var(--space-2)'
              }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={20} color="var(--color-muted)" style={{
                  position: 'absolute',
                  left: 'var(--space-3)',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }} />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    paddingLeft: 'var(--space-10)',
                    paddingRight: 'var(--space-12)',
                    paddingTop: 'var(--space-3)',
                    paddingBottom: 'var(--space-3)',
                    border: `1px solid ${errors.password ? 'var(--color-error)' : 'var(--color-border)'}`,
                    borderRadius: 'var(--radius-lg)',
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-base)',
                    color: 'var(--color-text)',
                    outline: 'none',
                    transition: 'var(--transition-fast)',
                    backgroundColor: isLoading ? 'var(--color-surface)' : 'var(--color-background)',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    if (!errors.password) {
                      e.currentTarget.style.borderColor = 'var(--color-primary)';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.password ? 'var(--color-error)' : 'var(--color-border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  style={{
                    position: 'absolute',
                    right: 'var(--space-3)',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 'var(--space-1)',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--color-muted)',
                    transition: 'var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-secondary)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-muted)'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <p style={{
                  marginTop: 'var(--space-1)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)'
                }}>
                  <AlertCircle size={16} />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember Me & Forgot Password */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="custom-checkbox"
                />
                <span style={{
                  marginLeft: 'var(--space-2)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-secondary)'
                }}>
                  Remember me
                </span>
              </label>
              <button
                type="button"
                onClick={onNavigateToForgotPassword}
                disabled={isLoading}
                style={{
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: '#667eea',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-medium)',
                  textDecoration: 'none',
                  transition: 'var(--transition-fast)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                Forgot password?
              </button>
            </div>

            {/* Terms & Conditions Checkbox */}
            <div>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                cursor: 'pointer',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-lg)',
                backgroundColor: errors.acceptedTerms ? '#fef2f2' : 'transparent',
                border: `1px solid ${errors.acceptedTerms ? 'var(--color-error)' : 'transparent'}`
              }}>
                <input
                  type="checkbox"
                  name="acceptedTerms"
                  checked={formData.acceptedTerms}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="custom-checkbox"
                  style={{
                    marginTop: '2px',
                    flexShrink: 0
                  }}
                />
                <span style={{
                  marginLeft: 'var(--space-2)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-secondary)',
                  lineHeight: '1.5'
                }}>
                  I agree to the{' '}
                  <a
                    href="#terms"
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsModalTab('terms');
                      setShowTermsModal(true);
                    }}
                    style={{
                      color: '#667eea',
                      textDecoration: 'underline',
                      fontWeight: 'var(--font-medium)',
                      cursor: 'pointer'
                    }}
                  >
                    Terms & Conditions
                  </a>
                  {' '}and{' '}
                  <a
                    href="#privacy"
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsModalTab('privacy');
                      setShowTermsModal(true);
                    }}
                    style={{
                      color: '#667eea',
                      textDecoration: 'underline',
                      fontWeight: 'var(--font-medium)',
                      cursor: 'pointer'
                    }}
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
              {errors.acceptedTerms && (
                <p style={{
                  marginTop: 'var(--space-2)',
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-error)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  paddingLeft: 'var(--space-3)'
                }}>
                  <AlertCircle size={16} />
                  {errors.acceptedTerms}
                </p>
              )}
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--color-background)',
                backgroundColor: isLoading ? 'var(--color-muted)' : 'var(--color-primary)',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-fast)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = '#1f2937';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.backgroundColor = 'var(--color-primary)';
                }
              }}
            >
              {isLoading ? (
                <>
                  <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>

            {/* Sign Up Link */}
            <div style={{
              textAlign: 'center',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid var(--color-border)'
            }}>
              <span style={{
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-secondary)'
              }}>
                Don't have an account?{' '}
              </span>
              <button
                type="button"
                onClick={onNavigateToRegister}
                disabled={isLoading}
                style={{
                  fontFamily: 'var(--font-primary)',
                  fontSize: 'var(--text-sm)',
                  color: '#667eea',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-medium)',
                  textDecoration: 'none',
                  transition: 'var(--transition-fast)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                Create Account
              </button>
            </div>
          </div>
        </div>

        {/* Terms Modal */}
        <TermsModal
          isOpen={showTermsModal}
          onClose={() => setShowTermsModal(false)}
          initialTab={termsModalTab}
        />
      </div>
    </div>
  );
};